import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import notificationService from '../services/notificationService';
import cloudinaryService from '../services/cloudinaryService';
import themeService from '../services/themeService';
import { formatCurrency, formatDate, getStatusColor, getStatusText } from '../utils/helpers';
import { Client, Request, Conversation, Ad } from '../models';
import { FiHome, FiVideoOff, FiInbox, FiImage, FiPlay, FiVolume2, FiArrowLeft, FiHash, FiList, FiTool, FiMessageSquare, FiUser, FiRefreshCw, FiEdit2, FiLogOut, FiHeadphones, FiBookOpen, FiShield, FiUpload, FiDollarSign, FiStar, FiCheck, FiX, FiPlus, FiTrash2, FiCamera, FiSettings, FiPhone, FiMapPin, FiSearch, FiVolumeX, FiMoon, FiSun, FiZap, FiMenu, FiBell, FiBook, FiShoppingCart, FiShoppingBag, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { NotificationBadge, useUnreadRequests } from './NotificationBadge';
import QrScannerModal from './QrScannerModal';
import NotificationsScreen from './NotificationsScreen';

// 🆕 معرّف حساب الدعم الخاص بالعميل — يُستخدم لإظهار شارة التوثيق وإخفاء المهنة
const CLIENT_SUPPORT_PROVIDER_ID = '664608fa-abce-4a04-9f0c-9f867cf4827f';

const ClientMainScreen = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // 🆕 إضافة
  const pendingRequestSentRef = useRef(false);
  
  const [client, setClient] = useState(null);
  const [requests, setRequests] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [requestSubTab, setRequestSubTab] = useState(0);
  const [editingProfile, setEditingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: ''
  });
const [lastRefreshTime, setLastRefreshTime] = useState(null);
const [isDarkMode, setIsDarkMode] = useState(themeService.isDarkMode());
const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
const [providerSearchId, setProviderSearchId] = useState('');
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  
  const [playingAdIndex, setPlayingAdIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [videoAds, setVideoAds] = useState([]);

  // States for Complaint Modal
  const [complaintModalOpen, setComplaintModalOpen] = useState(false);
  const [complaintRequest, setComplaintRequest] = useState(null);
  const [complaintReason, setComplaintReason] = useState('');
  const [submittingComplaint, setSubmittingComplaint] = useState(false);
  
  const videoRefs = useRef([]);
  const adIntervalRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
const requestStatusChannelRef = useRef(null);
const dataRefreshIntervalRef = useRef(null);
const { unreadCount, markAllAsRead } = useUnreadRequests(requests, 'client');
const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

// 🆕 جلب عدد الإشعارات غير المقروءة
useEffect(() => {
  const fetchUnreadNotifications = async () => {
    if (!user?.id || user.id === 'guest') return;
    try {
      const count = await supabaseService.getUnreadNotificationsCount(user.id);
      setUnreadNotificationsCount(count);
    } catch (e) {
      console.error('Error fetching unread notifications count:', e);
    }
  };
  fetchUnreadNotifications();
  // تحديث كل 30 ثانية
  const interval = setInterval(fetchUnreadNotifications, 30000);
  return () => clearInterval(interval);
}, [user?.id]);


  useEffect(() => {
  loadClientData();
  
  dataRefreshIntervalRef.current = setInterval(() => {
    loadClientData();
  }, 5000); // ✅ كل 5 ثواني (أفضل للأداء)
  
  const removeListener = themeService.addListener((theme) => {
    setIsDarkMode(theme === 'dark');
  });
  
  return () => {
    if (dataRefreshIntervalRef.current) {
      clearInterval(dataRefreshIntervalRef.current);
    }
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
    }
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (requestStatusChannelRef.current) {
      supabaseService.client.removeChannel(requestStatusChannelRef.current);
    }
    removeListener();
  };
}, []);

useEffect(() => {
  const handlePendingRequest = async () => {
    // 🔥 تحقق من أنه لم يتم إرساله مسبقاً
    if (pendingRequestSentRef.current) {
      console.log('⏭️ Pending request already processed, skipping...');
      return;
    }

    const pendingRequest = sessionStorage.getItem('pending_request');
    
    if (pendingRequest && client) {
      try {
        // 🔥 تعيين العلم فوراً لمنع التكرار
        pendingRequestSentRef.current = true;

        const requestData = JSON.parse(pendingRequest);
        console.log('📋 Found pending request:', requestData);
        
        toast.loading('جاري إرسال الطلب المعلق...', { id: 'pending-request' });
        
        // إرسال الطلب
        const newRequest = {
          clientId: user.id,
          providerId: requestData.providerId,
          serviceId: requestData.serviceId,
          status: 'pending'
        };
        
        await supabaseService.createRequest(newRequest);
        
        // إرسال إشعار للمزود
        const mockRequestForNotification = {
          id: 'new',
          client: { 
            fullName: client.fullName || user.email || 'عميل' 
          },
          service: { 
            name: requestData.serviceName || 'خدمة'
          },
          provider: { 
            fullName: requestData.providerName 
          }
        };
        
        await notificationService.notifyNewRequest(
          requestData.providerId, 
          mockRequestForNotification
        );
        
        // 🔥 حذف الطلب المعلق فوراً
        sessionStorage.removeItem('pending_request');
        
        toast.success('✅ تم إرسال طلبك بنجاح!', { id: 'pending-request' });
        
        // تحديث البيانات
        loadClientData();
        
      } catch (error) {
        console.error('❌ Error sending pending request:', error);
        toast.error('فشل إرسال الطلب. حاول مرة أخرى.', { id: 'pending-request' });
        
        // 🔥 إعادة تعيين العلم في حالة الفشل
        pendingRequestSentRef.current = false;
      }
    }
  };
  
  // الانتظار قليلاً لضمان تحميل بيانات العميل
  if (client && !pendingRequestSentRef.current) {
    setTimeout(handlePendingRequest, 1000);
  }
}, [client, user]);


useEffect(() => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then(permission => {
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('تم تفعيل الإشعارات بنجاح');
        // يمكنك تهيئة الإشعارات هنا إذا لزم الأمر
        // notificationService.initialize(user.id);
      } else if (permission === 'denied') {
        toast.error('تم رفض الإشعارات. يمكنك تفعيلها يدويًا من إعدادات المتصفح.');
      }
    });
  }
}, []);

// تمييز الطلبات كمقروءة عند فتح التبويبة
useEffect(() => {
  if (activeTab === 1) {
    markAllAsRead();
  }
}, [activeTab, markAllAsRead]);

  useEffect(() => {
    const videoOnlyAds = ads.filter(ad => ad.mediaType === 'video');
    setVideoAds(videoOnlyAds);
    
    if (videoOnlyAds.length > 0 && videoRefs.current[0]) {
      if (isPlaying) {
        videoRefs.current[0].play().catch(e => console.log("Auto-play prevented:", e));
      }
      
      adIntervalRef.current = setInterval(() => {
        setPlayingAdIndex(prev => {
          const nextIndex = (prev + 1) % videoOnlyAds.length;
          
          if (videoRefs.current[prev]) {
            videoRefs.current[prev].pause();
          }
          
          if (videoRefs.current[nextIndex] && isPlaying) {
            videoRefs.current[nextIndex].play().catch(e => console.log("Auto-play prevented:", e));
          }
          
          return nextIndex;
        });
      }, 15000);
    }
    
    return () => {
      if (adIntervalRef.current) {
        clearInterval(adIntervalRef.current);
      }
    };
  }, [ads, isPlaying]);

  const handleTabChange = async (tabId) => {
    if (user?.isGuest && tabId !== 0) {
      await signOut();
      navigate('/auth');
      toast.error('يجب تسجيل الدخول للوصول إلى هذه الصفحة');
      return;
    }
    setActiveTab(tabId);
  };

  const loadClientData = async () => {
    if (user?.isGuest) {
      setClient({
        id: 'guest',
        fullName: 'زائر (تصفح فقط)',
        avatarUrl: 'https://ui-avatars.com/api/?name=%D8%B2%D8%A7%D8%A6%D8%B1&background=1f2937&color=fff',
        phone: '',
        address: '',
        email: ''
      });
      setRequests([]);
      setConversations([]);
      const adsData = await fetchAds();
      setAds(adsData || []);
      setLoading(false);
      return;
    }
    try {
      const userId = user.id;

      // 🆕 Promise.allSettled بدل Promise.all — كل طلب مستقل لا يكسر الباقين
      const [
        clientResult,
        requestsResult,
        conversationsResult,
        adsResult
      ] = await Promise.allSettled([
        supabaseService.getClientById(userId),
        supabaseService.getRequestsForUser(userId, { isClient: true }),
        supabaseService.getConversationsForUser(userId),
        fetchAds()
      ]);

      // 🆕 كل قيمة تُستخرج بأمان بغض النظر عن نجاح أو فشل الباقين
      const clientData        = clientResult.status        === 'fulfilled' ? clientResult.value        : null;
      const requestsData      = requestsResult.status      === 'fulfilled' ? requestsResult.value      : [];
      const conversationsData = conversationsResult.status === 'fulfilled' ? conversationsResult.value : [];
      const adsData           = adsResult.status           === 'fulfilled' ? adsResult.value           : [];

      setClient(clientData);
      setRequests(requestsData || []);
      setConversations(conversationsData || []);
      setAds(adsData || []);
      
      if (clientData) {
        setFormData({
          fullName: clientData.fullName || '',
          phone: clientData.phone || '',
          address: clientData.address || ''
        });
      }
      
      setupRealtimeListeners(userId);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error("Error loading client data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDarkMode = () => {
    const newTheme = themeService.toggleTheme();
    setIsDarkMode(newTheme === 'dark');
    toast.success(`تم تفعيل الوضع ${newTheme === 'dark' ? 'المظلم' : 'الفاتح'}`);
  };

  const setupRealtimeListeners = (userId) => {
  };

  const fetchAds = async () => {
    // 🆕 offline — لا إعلانات بدون إنترنت
    if (!navigator.onLine) return [];

    try {
      const response = await supabaseService.client
          .from('ads')
          .select('*')
          .order('created_at', { ascending: false });

      // 🆕 حماية من null أو undefined
      if (!response.data || !Array.isArray(response.data)) return [];

      const ads = [];
      for (const item of response.data) {
        ads.push(Ad.fromJson(item));
      }
      return ads;
    } catch (e) {
      console.error('Error fetching ads:', e);
      return [];
    }
  };

  const handleVideoMute = (index) => {
    if (videoRefs.current[index]) {
      videoRefs.current[index].muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoEnded = (index) => {
    const nextIndex = (index + 1) % videoAds.length;
    
    if (videoRefs.current[index]) {
      videoRefs.current[index].pause();
    }
    
    setPlayingAdIndex(nextIndex);
    
    if (videoRefs.current[nextIndex] && isPlaying) {
      setTimeout(() => {
        videoRefs.current[nextIndex].play().catch(e => console.log("Auto-play prevented:", e));
      }, 500);
    }
  };

  const selectAd = (index) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
      }
    });
    
    if (videoRefs.current[playingAdIndex]) {
      videoRefs.current[playingAdIndex].pause();
    }
    
    setPlayingAdIndex(index);
    
    if (videoRefs.current[index] && isPlaying) {
      setTimeout(() => {
        videoRefs.current[index].play().catch(e => console.log("Auto-play prevented:", e));
      }, 500);
    }
    
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
    }
    
    adIntervalRef.current = setInterval(() => {
      const nextIndex = (index + 1) % videoAds.length;
      
      if (videoRefs.current[index]) {
        videoRefs.current[index].pause();
      }
      
      setPlayingAdIndex(nextIndex);
      
      if (videoRefs.current[nextIndex] && isPlaying) {
        videoRefs.current[nextIndex].play();
      }
    }, 15000);
  };

  const showVideoControls = () => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleVideoClick = () => {
    showVideoControls();
  };

  const updateProfile = async () => {
    try {
      await supabaseService.updateClient(client.id, {
        full_name: formData.fullName,
        phone: formData.phone,
        address: formData.address
      });
      
      setEditingProfile(false);
      loadClientData();
      toast.success('تم تحديث الملف الشخصي بنجاح');
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error('فشل تحديث الملف الشخصي');
    }
  };

  const pickAndUploadAvatar = async () => {
    if (uploadingAvatar) return;
    
    try {
      setUploadingAvatar(true);
      
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          const url = await cloudinaryService.uploadImage(file, 'avatar');
          if (url) {
            await supabaseService.updateClient(client.id, { avatar_url: url });
            loadClientData();
            toast.success('تم تحديث الصورة الشخصية');
          }
        }
        setUploadingAvatar(false);
      };
      
      input.click();
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error('فشل رفع الصورة الشخصية');
      setUploadingAvatar(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error('فشل تسجيل الخروج');
    }
  };

  const showCompletionDialog = (request) => {
    const confirmed = window.confirm('هل تم إنجاز الخدمة؟');
    
    if (confirmed) {
      showRatingDialog(request);
    }
  };

  const showRatingDialog = (request) => {
    const rating = parseInt(window.prompt('يرجى تقييم الخدمة (1-5):', '5'));
    
    if (rating >= 1 && rating <= 5) {
      const review = window.prompt('اكتب تعليقك (اختياري):', '');
      
      completeRequest(request.id, rating, review);
    } else {
      toast.error('يرجى إدخال تقييم بين 1 و 5');
    }
  };

  const completeRequest = async (requestId, rating, review) => {
    try {
      const request = requests.find(r => r.id === requestId);
      
      if (!request) {
        toast.error('لم يتم العثور على الطلب');
        return;
      }

      await supabaseService.completeAndRateRequest(requestId, rating, review);
      
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId ? { ...req, status: 'pending_provider_confirmation', rating, review } : req
        )
      );

      await notificationService.notifyRequestCompleted(request.providerId, request);
      
      toast.success('تم تأكيد إتمام الخدمة بنجاح، بانتظار موافقة المزود');
    } catch (error) {
      console.error("Error completing request:", error);
      toast.error('فشل إكمال الطلب');
    }
  };

  const navigateToChat = async (conversationId, providerName, providerAvatar, providerId) => {
    let finalConversationId = conversationId;

    if (!finalConversationId) {
      if (!providerId) {
        toast.error('خطأ: معرف المزود غير متوفر');
        return;
      }

      toast.loading('جاري إنشاء المحادثة...', { id: 'chat-create' });

      try {
        const newId = await supabaseService.getOrCreateConversation(user.id, providerId);
        
        if (newId) {
          finalConversationId = newId;
          toast.success('تم إنشاء المحادثة', { id: 'chat-create' });
        } else {
          toast.error('فشل إنشاء المحادثة', { id: 'chat-create' });
          return;
        }
      } catch (error) {
        console.error("Error creating conversation:", error);
        toast.error('حدث خطأ أثناء إنشاء المحادثة', { id: 'chat-create' });
        return;
      }
    }
    
    if (!finalConversationId) {
      toast.error('المحادثة غير متاحة حالياً');
      return;
    }
    
    navigate(`/chat/${finalConversationId}`, {
      state: {
        otherUserName: providerName,
        otherUserAvatarUrl: providerAvatar
      }
    });
  };

  const navigateToProvidersList = () => {
    navigate('/providers');
  };
  
  const navigateToGame = () => {
    navigate('/memory-game');
  };

  const navigateToFoodStore = () => {
    if (user?.isGuest || user?.id === 'guest') {
      toast.error('يجب عليك تسجيل الدخول أولاً');
      return;
    }
    navigate('/food-store');
  };

  const openComplaintDialog = (request) => {
    setComplaintRequest(request);
    setComplaintReason('');
    setComplaintModalOpen(true);
  };

  const handleSendComplaint = async () => {
    if (!complaintReason.trim()) {
      toast.error('الرجاء إدخال سبب الشكوى');
      return;
    }
    setSubmittingComplaint(true);
    try {
      await supabaseService.createComplaint({
        clientId: complaintRequest.clientId,
        providerId: complaintRequest.providerId,
        requestId: complaintRequest.id,
        reason: complaintReason.trim()
      });
      toast.success('تم تقديم الشكوى بنجاح، سنقوم بمراجعتها قريباً');
      setComplaintModalOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('فشل تقديم الشكوى، يرجى المحاولة لاحقاً');
    } finally {
      setSubmittingComplaint(false);
    }
  };
 
  const navigateToDigitalStore = () => {
    if (user?.isGuest || user?.id === 'guest') {
      toast.error('يجب عليك تسجيل الدخول أولاً');
      return;
    }
    navigate('/digital-store');
  };

  const navigateToStore = () => {
    if (user?.isGuest || user?.id === 'guest') {
      toast.error('يجب عليك تسجيل الدخول أولاً');
      return;
    }
    navigate('/store');
  };

const searchProviderById = async () => {
  if (providerSearchId.length !== 9) {
    toast.error('يرجى إدخال رقم ID صحيح مكون من 9 أرقام');
    return;
  }

  try {
    toast.loading('جاري البحث عن المزود...', { id: 'provider-search' });

    // البحث عن المزود باستخدام provider_id
    const { data, error } = await supabaseService.client
      .from('providers')
      .select('id')
      .eq('provider_id', providerSearchId)
      .maybeSingle();

    if (error) {
      console.error('Error searching provider:', error);
      toast.error('حدث خطأ أثناء البحث', { id: 'provider-search' });
      return;
    }

    if (!data) {
      toast.error('لم يتم العثور على مزود بهذا الرقم', { id: 'provider-search' });
      return;
    }

    toast.success('تم العثور على المزود!', { id: 'provider-search' });
    
    // الانتقال إلى صفحة المزود العامة
    navigate(`/provider-public/${data.id}`);
    
    // إعادة تعيين حقل البحث
    setProviderSearchId('');
  } catch (error) {
    console.error('Error searching provider:', error);
    toast.error('حدث خطأ أثناء البحث', { id: 'provider-search' });
  }
};

const handleSupportClick = async () => {
  if (user?.isGuest) {
    await signOut();
    navigate('/auth');
    toast.error('يجب تسجيل الدخول أولاً للتواصل مع الدعم');
    return;
  }
  const supportProviderId = CLIENT_SUPPORT_PROVIDER_ID;
  const supportName = "الدعم الفني";
  const supportAvatar = "https://mute-glitter-cfe7.mouhoubifares12.workers.dev/file/4_z9e3b76d808a9dbfd92cd0415_f101368a68b28f379_d20260718_m154057_c005_v0501031_t0003_u01784389257827?t=khidma_secret_2026_xyz";
  
  toast.loading('جاري الاتصال بالدعم...', { id: 'support-chat' });
  try {
    const conversationId = await supabaseService.getOrCreateConversation(user.id, supportProviderId);
    if (conversationId) {
      toast.success('تم فتح محادثة الدعم', { id: 'support-chat' });
      navigate(`/chat/${conversationId}`, {
        state: {
          otherUserName: supportName,
          otherUserAvatarUrl: supportAvatar
        }
      });
    } else {
      toast.error('فشل إنشاء محادثة الدعم', { id: 'support-chat' });
    }
  } catch (error) {
    console.error("Error opening support chat:", error);
    toast.error('حدث خطأ أثناء فتح المحادثة', { id: 'support-chat' });
  }
};

const formatRefreshTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('ar-SA', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const tabs = [
    {
  id: 0,
  name: 'الرئيسية',
  icon: FiHome,
  content: (
  <div className="h-full flex flex-col bg-gray-50 font-sans overflow-hidden">
    
    {/* ============ HEADER (Unified) ============ */}
    <div className="flex-shrink-0 bg-gradient-to-l from-gray-900 to-gray-800 text-white shadow-md z-20">
  <div className="p-3 md:px-5 md:py-3">
    <div className="flex items-center justify-between mb-2.5">
      {/* User Info */}
      <div className="flex items-center gap-2.5">
        <img
          src={client?.avatarUrl || 'https://via.placeholder.com/150'}
          alt="Profile"
          className="w-9 h-9 md:w-11 md:h-11 rounded-full border-2 border-white/30 shadow-sm"
        />
        <div>
          <h2 className="text-sm md:text-base font-bold leading-tight">مرحباً، {client?.fullName?.split(' ')[0] || "العميل"}</h2>
          <p className="text-[10px] md:text-xs text-gray-400 font-medium">تصفح الخدمات والعروض</p>
        </div>
      </div>
          
          {/* Header Actions */}
<div className="flex gap-1.5">
  {/* ✅ زر الشحن + الرصيد */}
  <button
    onClick={() => navigate('/wallet')}
    className="px-2.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 transition active:scale-95 flex items-center gap-1"
    title="شحن المحفظة"
  >
    <FiDollarSign className="text-emerald-300 w-3.5 h-3.5" />
    <span className="text-[10px] font-bold text-emerald-300 hidden lg:inline">
      {client?.wallet?.toLocaleString('ar-DZ') || '0'} د.ج
    </span>
    <span className="text-[10px] font-bold text-emerald-300 hidden lg:inline">شحن</span>
  </button>
  <button
    onClick={() => navigate('/personal-qr')}
    className="px-2.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 transition active:scale-95 flex items-center gap-1"
    title="رمز المحفظة"
  >
    <FiDollarSign className="text-emerald-300 w-3.5 h-3.5" />
    <span className="text-[10px] font-bold text-emerald-300 hidden lg:inline">الكود</span>
  </button>
  <button
    onClick={() => setQrScannerOpen(true)}
    className="px-2.5 py-1.5 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 transition active:scale-95 flex items-center gap-1"
    title="مسح رمز QR"
  >
    <FiCamera className="text-blue-300 w-3.5 h-3.5" />
    <span className="text-[10px] font-bold text-blue-300 hidden lg:inline">مسح</span>
  </button>
  <button
    onClick={loadClientData}
    disabled={loading}
    className={`p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition active:scale-95 ${loading ? 'animate-spin' : ''}`}
  >
    <FiRefreshCw className="text-white w-4 h-4" />
  </button>
  <button onClick={toggleDarkMode} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition active:scale-95">
    {isDarkMode ? <FiSun className="text-yellow-300 w-4 h-4" /> : <FiMoon className="text-gray-300 w-4 h-4" />}
  </button>
</div>
        </div>

  </div>
</div>

        {/* ============ MOBILE LAYOUT (Scrollable) ============ */}
    <div className="md:hidden flex-1 overflow-y-auto pb-4 custom-scrollbar">
      
      {/* Quick Actions */}
      <div className="grid grid-cols-5 gap-2 mx-4 mt-4">
        <button onClick={navigateToProvidersList} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 active:scale-95 transition-transform flex flex-col items-center justify-center gap-1.5 group">
          <div className="bg-gray-900 text-white p-2 rounded-full group-hover:bg-gray-800 transition shadow-sm">
            <FiSearch className="text-base" />
          </div>
          <span className="text-[10px] font-bold text-gray-800">البحث</span>
        </button>

        {/* ✅ زر جديد — متجر خدمة */}
        <button onClick={navigateToStore} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 active:scale-95 transition-transform flex flex-col items-center justify-center gap-1.5 group">
          <div className="bg-orange-100 text-orange-600 p-2 rounded-full group-hover:bg-orange-200 transition">
            <FiShoppingCart className="text-base" />
          </div>
          <span className="text-[10px] font-bold text-gray-800">المتجر</span>
        </button>

        <button onClick={navigateToDigitalStore} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 active:scale-95 transition-transform flex flex-col items-center justify-center gap-1.5 group">
          <div className="bg-indigo-100 text-indigo-600 p-2 rounded-full group-hover:bg-indigo-200 transition">
            <FiBookOpen className="text-base" />
          </div>
          <span className="text-[10px] font-bold text-gray-800">الرقمي</span>
        </button>

        <button onClick={navigateToFoodStore} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 active:scale-95 transition-transform flex flex-col items-center justify-center gap-1.5 group">
          <div className="bg-red-100 text-red-600 p-2 rounded-full group-hover:bg-red-200 transition">
            <FiShoppingBag className="text-base" />
          </div>
          <span className="text-[10px] font-bold text-gray-800">الأكل</span>
        </button>

        <button onClick={() => setQrScannerOpen(true)} className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 active:scale-95 transition-transform flex flex-col items-center justify-center gap-1.5 group">
          <div className="bg-blue-100 text-blue-600 p-2 rounded-full group-hover:bg-blue-200 transition">
            <FiCamera className="text-base" />
          </div>
          <span className="text-[10px] font-bold text-gray-800">مسح QR</span>
        </button>
      </div>

      {/* Notification Banner */}
      {notificationPermission !== 'granted' ? (
        <div className="mx-4 mt-3 bg-blue-600 text-white p-3 rounded-xl shadow-md flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FiBell className="text-lg" />
            <span className="text-xs font-bold">فعّل الإشعارات لمتابعة الطلبات</span>
          </div>
          <button
            onClick={() => {
              Notification.requestPermission().then(permission => {
                setNotificationPermission(permission);
                permission === 'granted' ? toast.success('تم التفعيل') : toast.error('تم الرفض');
              });
            }}
            className="bg-white text-blue-600 text-xs px-3 py-1.5 rounded-lg font-bold shadow-sm whitespace-nowrap"
          >
            تفعيل
          </button>
        </div>
      ) : (
        <div className="mx-4 mt-3 bg-green-50 border border-green-200 text-green-800 p-3 rounded-xl flex items-center gap-2">
           <FiCheck className="text-lg text-green-600" />
           <span className="text-xs font-bold">الإشعارات مفعلة</span>
         </div>
      )}

      {/* Video Ads Section (Mobile Optimized) */}
<div className="mt-4 px-4">
  <div className="flex justify-between items-end mb-2.5">
     <h3 className="text-sm font-bold text-gray-800">إعلانات مميزة</h3>
  </div>
        
        {videoAds.length > 0 ? (
          <div 
  className="relative w-full h-40 bg-gray-900 rounded-xl overflow-hidden shadow-md group cursor-pointer"
            onClick={() => {
              if (videoRefs.current[playingAdIndex]) {
                const videoEl = videoRefs.current[playingAdIndex];
                if (isPlaying) {
                  videoEl.pause();
                  setIsPlaying(false);
                } else {
                  // محاولة التشغيل، إذا فشل نحاول كتم الصوت (مهم للموبايل)
                  const playPromise = videoEl.play();
                  if (playPromise !== undefined) {
                    playPromise.then(_ => {
                      setIsPlaying(true);
                    }).catch(error => {
                      console.log("Autoplay prevented, muting and trying again");
                      setIsMuted(true);
                      videoEl.muted = true;
                      videoEl.play().then(() => setIsPlaying(true));
                    });
                  }
                }
              }
            }}
          >
            {videoAds.map((ad, index) => (
              <video
                key={ad.id}
                ref={el => videoRefs.current[index] = el}
                src={ad.mediaUrl}
                className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${playingAdIndex === index ? 'opacity-100' : 'opacity-0'}`}
                muted={isMuted}
                onEnded={() => handleVideoEnded(index)}
                playsInline // ضروري جداً للموبايل
                webkit-playsinline="true" // للموبايل القديم
                preload="metadata"
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
              <h4 className="text-white font-bold text-lg mb-1 drop-shadow-md">{videoAds[playingAdIndex]?.title}</h4>
              <p className="text-gray-200 text-xs line-clamp-1 drop-shadow-sm">{videoAds[playingAdIndex]?.description}</p>
            </div>
            {!isPlaying && (
               <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-black/10 backdrop-blur-[1px]">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full border border-white/30"><FiPlay className="text-white text-3xl ml-1" /></div>
               </div>
            )}
            <div className="absolute top-3 right-3 z-30 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
               {playingAdIndex + 1} / {videoAds.length}
            </div>
          </div>
        ) : (
          <div className="h-40 bg-gray-200 rounded-2xl flex flex-col items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-300">
            <FiVideoOff className="text-2xl mb-2" />
            لا توجد إعلانات فيديو حالياً
          </div>
        )}
      </div>

      {/* Image Ads Carousel */}
      {ads.filter(ad => ad.mediaType === 'image').length > 0 && (
        <div className="mt-4 px-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">عروض وصور</h3>
          <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x">
            {ads.filter(ad => ad.mediaType === 'image').map((ad) => (
              <div key={ad.id} className="flex-shrink-0 w-60 snap-center bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition">
                <div className="h-32 relative">
                  <img src={ad.mediaUrl} alt={ad.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <h4 className="absolute bottom-2 right-2 font-bold text-white text-sm drop-shadow-md">{ad.title}</h4>
                </div>
                <div className="p-3">
                  <p className="text-gray-600 text-xs line-clamp-2 leading-relaxed">{ad.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

     {/* Recent Requests */}
<div className="mt-4 mx-4 mb-4">
  <div className="flex justify-between items-center mb-2.5">
    <h3 className="text-sm font-bold text-gray-800">طلباتك الأخيرة</h3>
          <button onClick={() => handleTabChange(1)} className="text-xs text-blue-600 font-bold">عرض الكل</button>
        </div>

        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="bg-white p-8 rounded-xl border border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400">
              <FiInbox className="text-3xl mb-2 opacity-50" />
              <p className="text-sm font-medium">لا توجد طلبات حالياً</p>
            </div>
          ) : (
            requests.slice(0, 3).map((request) => (
              <div key={request.id} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-md transition">
                <div className="flex items-center gap-3">
                  <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 text-gray-600">
                    <FiList className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{request.service?.name || 'خدمة'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{request.provider?.fullName || "مزود خدمة"}</p>
                    <div className="mt-1">
                      {request.paymentMethod === 'wallet' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
                          💰 محفظة
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                          💵 كاش
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-2.5 py-1 rounded-md text-[10px] font-bold border" style={{ color: getStatusColor(request.status), borderColor: getStatusColor(request.status), backgroundColor: getStatusColor(request.status) + '10' }}>
                  {getStatusText(request.status)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>

    {/* ============ DESKTOP LAYOUT (Fixed Grid with Scrollable Sidebar) ============ */}
    <div className="hidden md:flex flex-1 gap-4 p-4 overflow-hidden bg-gray-100/50">
      
      {/* COLUMN 1: Sidebar (Actions & Notification) - Width: 25% - SCROLLABLE FIX */}
      <div className="w-1/4 flex flex-col gap-4 flex-shrink-0 overflow-y-auto custom-scrollbar pr-2">
         
         {/* Quick Actions Cards — Grid 2×3، كل الأزرار ظاهرة بلا نزول */}
<div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex-shrink-0">
   <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">تصفح سريع</h3>

   <div className="grid grid-cols-2 gap-2.5">
     <button onClick={navigateToProvidersList} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-gray-50 border border-gray-100 transition group text-center">
       <div className="bg-gray-900 text-white p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiSearch className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">بحث</span>
     </button>

     <button onClick={navigateToStore} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-orange-50 border border-gray-100 transition group text-center">
       <div className="bg-orange-100 text-orange-600 p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiShoppingCart className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">المتجر</span>
     </button>

     <button onClick={navigateToDigitalStore} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-indigo-50 border border-gray-100 transition group text-center">
       <div className="bg-indigo-100 text-indigo-600 p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiBookOpen className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">الرقمي</span>
     </button>

     <button onClick={navigateToFoodStore} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-red-50 border border-gray-100 transition group text-center">
       <div className="bg-red-100 text-red-600 p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiShoppingBag className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">الأكل</span>
     </button>

     <button onClick={() => setQrScannerOpen(true)} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-blue-50 border border-gray-100 transition group text-center">
       <div className="bg-blue-100 text-blue-600 p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiCamera className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">مسح</span>
     </button>

     <button onClick={() => navigate('/personal-qr')} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl hover:bg-emerald-50 border border-gray-100 transition group text-center">
       <div className="bg-emerald-100 text-emerald-600 p-2.5 rounded-xl group-hover:scale-105 transition-transform">
         <FiDollarSign className="text-lg" />
       </div>
       <span className="text-xs font-bold text-gray-800">الكود</span>
     </button>
   </div>
</div>
         {/* Notification Banner (Desktop) */}
         {notificationPermission !== 'granted' ? (
            <div className="bg-blue-600 text-white p-5 rounded-2xl shadow-lg flex flex-col items-start gap-3 flex-shrink-0">
              <div className="flex items-center gap-3 w-full">
                <div className="bg-white/20 p-2 rounded-full"><FiBell className="text-xl" /></div>
                <h3 className="font-bold text-lg">تفعيل الإشعارات</h3>
              </div>
              <p className="text-sm text-blue-100 leading-relaxed">لا تفوت تحديثات حالة طلباتك، فعّل التنبيهات الآن.</p>
              <button 
                onClick={() => Notification.requestPermission().then(p => { setNotificationPermission(p); p==='granted'?toast.success('تم'):toast.error('رفض'); })} 
                className="w-full bg-white text-blue-600 py-2.5 rounded-xl font-bold hover:bg-gray-100 transition mt-1"
              >
                تفعيل الآن
              </button>
            </div>
         ) : (
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-green-100 flex items-center gap-3 flex-shrink-0">
               <div className="bg-green-100 p-2.5 rounded-full text-green-600"><FiCheck className="text-xl" /></div>
               <div>
                 <h3 className="font-bold text-gray-800">الإشعارات مفعلة</h3>
                 <p className="text-xs text-gray-500">أنت متصل بآخر التحديثات</p>
               </div>
            </div>
         )}
      </div>

      {/* COLUMN 2: Ads Section (Video & Images) - Width: 45% */}
      <div className="w-[45%] flex flex-col gap-4 min-w-0 overflow-y-auto custom-scrollbar pr-1">
         
         {/* Main Video Ad */}
         <div className="bg-white p-2 rounded-2xl shadow-sm border border-gray-200 flex-shrink-0">
           <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden group cursor-pointer"
             onClick={() => {
                if (videoRefs.current[playingAdIndex]) {
                  isPlaying ? videoRefs.current[playingAdIndex].pause() : videoRefs.current[playingAdIndex].play();
                  setIsPlaying(!isPlaying);
                }
             }}
           >
             {videoAds.length > 0 ? (
               <>
                 {videoAds.map((ad, index) => (
                    <video
                      key={ad.id}
                      ref={el => videoRefs.current[index] = el}
                      src={ad.mediaUrl}
                      className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${playingAdIndex === index ? 'opacity-100' : 'opacity-0'}`}
                      muted={isMuted}
                      onEnded={() => handleVideoEnded(index)}
                      playsInline
                    />
                 ))}
                 <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10 pointer-events-none" />
                 <div className="absolute bottom-4 right-4 z-20 max-w-md">
                    <h4 className="text-white font-bold text-2xl mb-1 drop-shadow-lg">{videoAds[playingAdIndex]?.title}</h4>
                    <p className="text-gray-200 text-sm line-clamp-1 drop-shadow-md">{videoAds[playingAdIndex]?.description}</p>
                 </div>
                 {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none bg-black/20 backdrop-blur-sm">
                       <div className="bg-white/20 backdrop-blur-md p-4 rounded-full ring-1 ring-white/30">
                          <FiPlay className="text-white text-4xl ml-1" />
                       </div>
                    </div>
                 )}
               </>
             ) : (
               <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gray-800">
                 <FiVideoOff className="text-3xl mb-2 opacity-50" />
                 <span>لا توجد إعلانات فيديو</span>
               </div>
             )}
           </div>
         </div>

         {/* Image Ads Carousel for Desktop */}
         <div className="flex-1 min-h-[200px]">
            <div className="flex justify-between items-center mb-3 px-1">
               <h3 className="text-base font-bold text-gray-800">صور العروض</h3>
               <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{ads.filter(a => a.mediaType === 'image').length} عرض</span>
            </div>
            
            {ads.filter(ad => ad.mediaType === 'image').length > 0 ? (
               <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-[400px] pr-1 custom-scrollbar pb-2">
                 {ads.filter(ad => ad.mediaType === 'image').map((ad) => (
                   <div key={ad.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition group">
                     <div className="h-32 relative overflow-hidden">
                       <img src={ad.mediaUrl} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                       <h4 className="absolute bottom-2 right-2 font-bold text-white text-sm drop-shadow-md">{ad.title}</h4>
                     </div>
                     <div className="p-3">
                       <p className="text-gray-600 text-xs line-clamp-2 leading-relaxed">{ad.description}</p>
                     </div>
                   </div>
                 ))}
               </div>
            ) : (
               <div className="h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400">
                  <FiImage className="text-2xl mb-2 opacity-50" />
                  <span className="text-sm">لا توجد صور عروض</span>
               </div>
            )}
         </div>
      </div>

      {/* COLUMN 3: Requests History - Width: 30% */}
      <div className="w-[30%] bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col min-h-0 overflow-hidden">
         <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <h3 className="text-lg font-bold text-gray-800">سجل الطلبات</h3>
            <button onClick={() => handleTabChange(1)} className="text-sm text-blue-600 font-bold hover:underline">عرض الكل</button>
         </div>
         
         <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
           {requests.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 py-10">
                <div className="bg-gray-50 p-4 rounded-full mb-3">
                  <FiList className="text-3xl opacity-30" />
                </div>
                <p className="text-sm font-bold text-gray-300">لا توجد طلبات</p>
              </div>
           ) : (
              requests.map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition border border-gray-100 group">
                  <div className="flex items-center gap-3">
                    <div className="bg-white p-2.5 rounded-lg shadow-sm border border-gray-100 text-gray-600 group-hover:text-gray-900 transition">
                      <FiList className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{request.service?.name}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{request.provider?.fullName}</p>
                      <div className="mt-1">
                        {request.paymentMethod === 'wallet' ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
                            💰 محفظة
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                            💵 كاش
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-1.5 rounded-lg text-[10px] font-bold border whitespace-nowrap" style={{ color: getStatusColor(request.status), borderColor: getStatusColor(request.status), backgroundColor: getStatusColor(request.status) + '10' }}>
                    {getStatusText(request.status)}
                  </div>
                </div>
              ))
           )}
         </div>
      </div>
    </div>
  </div>
)
    },
    {
      id: 1,
      name: 'الطلبات',
      icon: FiList,
      content: (
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">طلباتي</h2>
            <button
              onClick={loadClientData}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiRefreshCw className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-900">
              {['معلق', 'مقبول', 'مرفوض', 'مكتمل'].map((status, index) => (
                <button
                  key={status}
                  className={`flex-1 py-3 text-center font-medium transition-colors text-sm ${
                    requestSubTab === index
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => setRequestSubTab(index)}
                >
                  {status}
                </button>
              ))}
            </div>

            <div className="p-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <AnimatePresence>
                  {requests
                    .filter(request => {
                      const status = request.status?.toLowerCase().trim();
                      switch (requestSubTab) {
                        case 0: return status === 'pending';
                        case 1: return status === 'accepted' || status === 'pending_provider_confirmation';
                        case 2: return status === 'rejected';
                        case 3: return status === 'completed';
                        default: return false;
                      }
                    })
                    .map((request) => (
                      <motion.div
                        key={request.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4 bg-gray-50 p-3 border-b border-gray-900 last:border-b-0"
                      >
                        {['accepted', 'pending_provider_confirmation'].includes(request.status?.toLowerCase().trim()) ? (
                          <div>
                            <div className="flex items-center space-x-3 space-x-reverse mb-3">
                              <img
                                src={request.provider?.avatarUrl || 'https://via.placeholder.com/150'}
                                alt="Provider"
                                className="w-10 h-10 rounded-full"
                              />
                              <div>
                                <p className="font-medium text-sm">{request.provider?.fullName || 'مزود'}</p>
                                <p className="text-xs text-gray-500">{request.provider?.professionName || 'مزود خدمة'}</p>
                              </div>
                            </div>
                        <div className="bg-white p-3 rounded-lg mb-3">
                          <p className="font-medium mb-1 text-sm">{request.service?.name || 'خدمة'}</p>
                          <p className="text-xs text-gray-500">السعر: {formatCurrency(request.service?.price || 0)}</p>
                          <div className="mt-2">
                            {request.paymentMethod === 'wallet' ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                💰 محفظة
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                💵 كاش
                              </span>
                            )}
                          </div>
                        </div>
                            <div className="bg-green-50 p-3 rounded-lg mb-3 flex items-center space-x-2 space-x-reverse">
                              <FiMessageSquare className="text-green-600" />
                              <div>
                                <p className="font-medium text-green-800 text-sm">تواصل مع المزود</p>
                                <p className="text-green-700 text-xs">
                                  رقم الهاتف: {request.provider?.phone ? (
                                    <a href={`tel:${request.provider.phone}`} className="text-green-950 font-bold underline hover:text-black dir-ltr inline-block">
                                      {request.provider.phone}
                                    </a>
                                  ) : 'غير متوفر'}
                                </p>
                                <p className="text-green-900 text-xs font-bold mt-0.5">العنوان: {request.provider?.address || request.provider?.stateName || 'غير محدد'}</p>
                              </div>
                            </div>
                            <div className="flex space-x-3 space-x-reverse">
                              <button
                                onClick={() => navigateToChat(
                                  conversations.find(c => c.providerId === request.providerId)?.id,
                                  request.provider?.fullName,
                                  request.provider?.avatarUrl,
                                  request.providerId
                                )}
                                className="flex-1 bg-gray-900 text-white py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors text-sm"
                              >
                                دردشة الآن
                              </button>
                              {request.status?.toLowerCase().trim() === 'accepted' ? (
                                <button
                                  onClick={() => showCompletionDialog(request)}
                                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                                >
                                  تمت الخدمة
                                </button>
                              ) : (
                                <button
                                  disabled
                                  className="flex-1 bg-gray-300 text-gray-600 py-2 rounded-lg font-medium text-sm cursor-not-allowed"
                                >
                                  بانتظار تأكيد المزود
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3 space-x-reverse">
                                <div className="bg-gray-900 rounded-full p-2">
                                  <FiList className="text-white text-sm" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{request.service?.name || 'خدمة'}</p>
                                  <p className="text-xs text-gray-500">المزود: {request.provider?.fullName || "غير معروف"}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 space-x-reverse">
                                <p className="text-xs text-gray-500">
                                  السعر: {formatCurrency(request.service?.price || 0)}
                                </p>
                                {request.paymentMethod === 'wallet' ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                    💰 محفظة
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                    💵 كاش
                                  </span>
                                )}
                                <div
                                  className="px-3 py-1 rounded-full text-[10px] font-medium text-white"
                                  style={{ backgroundColor: getStatusColor(request.status) }}
                                >
                                  {getStatusText(request.status)}
                                </div>
                              </div>
                            </div>
                            {request.status?.toLowerCase().trim() === 'completed' && (
                              <div className="flex justify-end">
                                <button
                                  onClick={() => openComplaintDialog(request)}
                                  className="bg-red-50 text-red-600 px-4 py-1.5 rounded-lg border border-red-200 text-xs font-semibold hover:bg-red-100 transition-colors"
                                >
                                  تقديم شكوى
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 2,
      name: 'الدردشة',
      icon: FiMessageSquare,
      content: (
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">المحادثات</h2>
            <button
              onClick={loadClientData}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiRefreshCw className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-gray-100 rounded-full p-6 mb-4">
                <FiMessageSquare className="text-4xl text-gray-400" />
              </div>
              <p className="text-xl font-medium text-gray-600 mb-2">لا توجد محادثات بعد</p>
              <p className="text-gray-500">سيظهر هنا محادثاتك مع مقدمي الخدمات</p>
            </div>
          ) : (
            <div className="space-y-4">
              {conversations.map((conversation) => {
                // 🆕 التحقق مما إذا كانت هذه محادثة حساب الدعم
                const isSupportAccount =
                  conversation.providerId === CLIENT_SUPPORT_PROVIDER_ID ||
                  conversation.provider?.id === CLIENT_SUPPORT_PROVIDER_ID;

                return (
                <div
                  key={conversation.id}
                  className="bg-white rounded-lg p-4 shadow-md flex items-center space-x-3 space-x-reverse cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => navigateToChat(
                    conversation.id,
                    conversation.provider?.fullName,
                    conversation.provider?.avatarUrl
                  )}
                >
                  <img
                    src={conversation.provider?.avatarUrl || 'https://via.placeholder.com/150'}
                    alt="Provider"
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium">{conversation.provider?.fullName || 'مزود'}</p>
                      {isSupportAccount && (
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"
                          title="حساب موثّق"
                        >
                          <FiCheck className="text-white" style={{ fontSize: '10px' }} />
                        </span>
                      )}
                    </div>
                    {/* 🆕 إخفاء المهنة لحساب الدعم فقط */}
                    {!isSupportAccount && (
                      <p className="text-sm text-gray-500">{conversation.provider?.professionName || 'مزود خدمة'}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {conversation.provider?.phone ? (
                        <a href={`tel:${conversation.provider.phone}`} className="text-blue-600 font-medium underline hover:text-blue-800 dir-ltr inline-block">
                          {conversation.provider.phone}
                        </a>
                      ) : 'لا يوجد رقم هاتف'}
                    </p>
                  </div>
                  <div className="bg-gray-900 rounded-full p-2">
                    <FiMessageSquare className="text-white" />
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )
    },
    {
      id: 3,
      name: 'الإشعارات',
      icon: FiBell,
      content: (
        <NotificationsScreen userType="client" />
      )
    },
    {
      id: 4,
      name: 'الملف الشخصي',
      icon: FiUser,
      content: (
        // 🆕 تصميم مضغوط: كل العناصر تظهر في الشاشة الأولى بدون الحاجة للتمرير
        <div className="p-3 h-full flex flex-col gap-2.5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">الملف الشخصي</h2>
            <button
              onClick={loadClientData}
              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiRefreshCw className={`text-gray-600 w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* رأس الملف الشخصي المضغوط */}
          <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-3 text-white shadow-lg flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <img
                src={client?.avatarUrl || 'https://via.placeholder.com/150'}
                alt="Profile"
                className="w-14 h-14 rounded-full border-2 border-white"
              />
              {!user?.isGuest && (
                <button
                  onClick={pickAndUploadAvatar}
                  className="absolute -bottom-1 -left-1 bg-white rounded-full p-1 shadow-lg"
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-900"></div>
                  ) : (
                    <FiCamera className="text-gray-900 w-3 h-3" />
                  )}
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold truncate">{client?.fullName || 'اسم العميل'}</h2>
              <div className="flex items-center gap-1.5 mt-1 bg-white/10 px-2 py-1 rounded-lg w-fit">
                <FiHash className="text-white/70 w-3 h-3" />
                <span className="font-mono text-white/90 text-[11px] tracking-wider">
                  ID: {client?.clientId || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* المعلومات الشخصية */}
          <div className="bg-white rounded-xl p-3 shadow-md">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold">معلوماتي الشخصية</h3>
              <button
                onClick={() => setEditingProfile(!editingProfile)}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                {editingProfile ? 'إلغاء' : 'تعديل'}
              </button>
            </div>

            {editingProfile ? (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="الاسم الكامل"
                  className="w-full py-1.5 px-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                />
                <input
                  type="tel"
                  placeholder="رقم الهاتف"
                  className="w-full py-1.5 px-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="العنوان"
                  className="w-full py-1.5 px-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
                <button
                  onClick={updateProfile}
                  className="w-full bg-gray-900 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  حفظ التغييرات
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <div className="bg-gray-900 rounded-full p-1.5 flex-shrink-0"><FiPhone className="text-white w-3 h-3" /></div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-500">الهاتف</p>
                    <p className="text-xs font-medium truncate">{client?.phone || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <div className="bg-gray-900 rounded-full p-1.5 flex-shrink-0"><FiEdit2 className="text-white w-3 h-3" /></div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-500">البريد</p>
                    <p className="text-xs font-medium truncate">{client?.email || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <div className="bg-gray-900 rounded-full p-1.5 flex-shrink-0"><FiMapPin className="text-white w-3 h-3" /></div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-500">العنوان</p>
                    <p className="text-xs font-medium truncate">{client?.address || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-gray-900 rounded-full p-1.5 flex-shrink-0">
                      {isDarkMode ? <FiMoon className="text-white w-3 h-3" /> : <FiSun className="text-white w-3 h-3" />}
                    </div>
                    <p className="text-xs font-medium truncate">الوضع المظلم</p>
                  </div>
                  <button
                    onClick={toggleDarkMode}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                        isDarkMode ? 'translate-x-3.5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* روابط سريعة */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={async () => {
                if (user?.isGuest) {
                  await signOut();
                  navigate('/auth');
                  toast.error('يجب تسجيل الدخول أولاً للوصول إلى بطاقة العضوية');
                  return;
                }
                navigate('/member-card');
              }}
              className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100 flex items-center gap-2 hover:shadow-md transition text-right"
            >
              <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full p-1.5 flex-shrink-0">
                <FiHash className="text-white w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-gray-800 truncate">بطاقة العضوية</span>
            </button>

            <button
              onClick={async () => {
                if (user?.isGuest) {
                  await signOut();
                  navigate('/auth');
                  toast.error('يجب تسجيل الدخول أولاً للوصول إلى مكتبتك');
                  return;
                }
                navigate('/my-library');
              }}
              className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100 flex items-center gap-2 hover:shadow-md transition text-right"
            >
              <div
                className="rounded-full p-1.5 flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #8b5cf6 100%)' }}
              >
                <FiBook className="text-white w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-gray-800 truncate">مكتبتي الرقمية</span>
            </button>

            <Link
              to="/contact-us"
              className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100 flex items-center gap-2 hover:shadow-md transition"
            >
              <div className="bg-blue-100 rounded-full p-1.5 flex-shrink-0">
                <FiHeadphones className="text-blue-600 w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-gray-800 truncate">التواصل معنا</span>
            </Link>

            <Link
              to="/rules-and-terms"
              className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100 flex items-center gap-2 hover:shadow-md transition"
            >
              <div className="bg-orange-100 rounded-full p-1.5 flex-shrink-0">
                <FiBookOpen className="text-orange-600 w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-gray-800 truncate">القواعد والقوانين</span>
            </Link>

            <Link
              to="/privacy-policy"
              className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100 flex items-center gap-2 hover:shadow-md transition col-span-2 sm:col-span-1"
            >
              <div className="bg-emerald-100 rounded-full p-1.5 flex-shrink-0">
                <FiShield className="text-emerald-600 w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-bold text-gray-800 truncate">سياسة الخصوصية</span>
            </Link>
          </div>

          {/* حذف الحساب + تسجيل الخروج */}
          <div className="grid grid-cols-2 gap-2 mt-auto">
            {!user?.isGuest ? (
              <Link
                to="/delete-account"
                className="bg-white rounded-xl p-2.5 shadow-sm border border-red-100 flex items-center justify-center gap-1.5 hover:shadow-md transition"
              >
                <FiTrash2 className="text-red-600 w-3.5 h-3.5" />
                <span className="text-xs font-bold text-red-600">حذف الحساب</span>
              </Link>
            ) : (
              <div className="bg-gray-100 rounded-xl p-2.5 border border-gray-200 flex items-center justify-center gap-1.5 text-gray-400 select-none">
                <FiShield className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">حساب ضيف</span>
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="bg-red-600 text-white rounded-xl p-2.5 shadow-sm flex items-center justify-center gap-1.5 hover:bg-red-700 transition"
            >
              <FiLogOut className="w-3.5 h-3.5" />
              <span className="text-xs font-bold">تسجيل الخروج</span>
            </button>
          </div>
        </div>
      )
    }
  ];

  if (loading && !client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // 🆕 بدون إنترنت ولا كاش — رسالة مناسبة بدل "حدث خطأ"
  if (!client) {
    if (!navigator.onLine) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-6">
            <div className="text-5xl mb-4">📴</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">أنت غير متصل بالإنترنت</h2>
            <p className="text-gray-500 text-sm mb-4">لم يتم العثور على بيانات محفوظة. يرجى الاتصال بالإنترنت مرة واحدة أولاً.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">حدث خطأ، لم يتم العثور على بيانات العميل.</h2>
          <button
            onClick={loadClientData}
            className="bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Design */}
      <div className="md:hidden h-screen w-full bg-gray-100 flex items-center justify-center overflow-hidden">
        <div className="max-w-md w-full h-full bg-white flex flex-col relative shadow-2xl">
          <div className="flex-1 overflow-y-auto pb-20 scroll-smooth">
            {tabs[activeTab].content}
          </div>
          <div className="bg-white border-t border-gray-200 w-full shrink-0 h-16 fixed bottom-0 left-0 right-0 z-50 shadow-lg">
            <div className="flex h-full">
              {tabs.map((tab) => (
                <button
  key={tab.id}
  className={`flex-1 flex flex-col items-center justify-center transition-colors relative ${
    activeTab === tab.id
      ? 'text-gray-900'
      : 'text-gray-600 hover:text-gray-900'
  }`}
  onClick={() => handleTabChange(tab.id)}
>
  <tab.icon className="text-xl mb-1" />
  <span className="text-xs">{tab.name}</span>
  {tab.id === 1 && <NotificationBadge count={unreadCount} />}
  {tab.id === 3 && <NotificationBadge count={unreadNotificationsCount} />}
</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Design */}
      <div className="hidden md:flex h-screen bg-gray-50 overflow-hidden">
        <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col h-full`}>
          <div className="p-3 border-b border-gray-700 flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center md:justify-start space-x-2 space-x-reverse hover:bg-gray-800 p-2 rounded-lg transition-colors"
            >
              <FiMenu className="text-xl" />
              {sidebarOpen && <span className="font-medium">القائمة</span>}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-800">
            {tabs.map((tab) => (
  <button
    key={tab.id}
    onClick={() => handleTabChange(tab.id)}
    className={`w-full flex items-center space-x-3 space-x-reverse p-2.5 rounded-lg mb-1 transition-colors relative ${
      activeTab === tab.id
        ? 'bg-gray-800 text-white'
        : 'text-gray-300 hover:bg-gray-800'
    }`}
  >
    <tab.icon className="text-lg flex-shrink-0" />
    {sidebarOpen && (
      <>
        <span className="text-sm font-medium">{tab.name}</span>
        {tab.id === 1 && unreadCount > 0 && (
          <span className="mr-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {tab.id === 3 && unreadNotificationsCount > 0 && (
          <span className="mr-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
          </span>
        )}
      </>
    )}
    {!sidebarOpen && tab.id === 1 && unreadCount > 0 && (
      <NotificationBadge count={unreadCount} className="top-1 right-1" />
    )}
    {!sidebarOpen && tab.id === 3 && unreadNotificationsCount > 0 && (
      <NotificationBadge count={unreadNotificationsCount} className="top-1 right-1" />
    )}
  </button>
))}
          </div>

          {sidebarOpen && (
            <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2">
              <div className="flex items-center space-x-3 space-x-reverse">
                <img
                  src={client?.avatarUrl || 'https://via.placeholder.com/150'}
                  alt="Profile"
                  className="w-9 h-9 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{client?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{client?.email}</p>
                </div>
              </div>
              
              <button
                onClick={toggleDarkMode}
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                {isDarkMode ? <FiSun className="text-yellow-300 text-base" /> : <FiMoon className="text-base" />}
                <span>{isDarkMode ? 'الوضع الفاتح' : 'الوضع المظلم'}</span>
              </button>

              <Link
                to="/contact-us"
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                <FiHeadphones className="text-base" />
                <span>التواصل معنا</span>
              </Link>

              <Link
                to="/rules-and-terms"
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                <FiBookOpen className="text-base" />
                <span>القواعد والقوانين</span>
              </Link>

              <Link
                to="/privacy-policy"
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                <FiShield className="text-base text-emerald-400" />
                <span>سياسة الخصوصية</span>
              </Link>

              {!user?.isGuest && (
                <Link
                  to="/delete-account"
                  className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
                >
                  <FiTrash2 className="text-base" />
                  <span>حذف الحساب</span>
                </Link>
              )}

              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-2 space-x-reverse p-2 rounded-lg hover:bg-red-700 bg-red-600 transition-colors text-sm"
              >
                <FiLogOut className="text-base" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col overflow-hidden h-full">
          <div className="bg-white border-b border-gray-200 p-3 flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
            <h1 className="text-xl font-bold text-gray-900">{tabs[activeTab].name}</h1>

            {/* ✅ كل أزرار التصفح السريع مباشرة في الهيدر — بلا حاجة للنزول */}
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={navigateToProvidersList} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition text-xs font-bold text-gray-700">
                <FiSearch className="text-sm" />
                <span>بحث</span>
              </button>
              <button onClick={navigateToStore} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 transition text-xs font-bold text-orange-700">
                <FiShoppingCart className="text-sm" />
                <span>المتجر</span>
              </button>
              <button onClick={navigateToDigitalStore} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition text-xs font-bold text-indigo-700">
                <FiBookOpen className="text-sm" />
                <span>الرقمي</span>
              </button>
              <button onClick={navigateToFoodStore} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 transition text-xs font-bold text-red-700">
                <FiShoppingBag className="text-sm" />
                <span>الأكل</span>
              </button>
              <button onClick={() => navigate('/personal-qr')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition text-xs font-bold text-emerald-700">
                <FiDollarSign className="text-sm" />
                <span>الكود</span>
              </button>
              <button onClick={() => setQrScannerOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 transition text-xs font-bold text-blue-700">
                <FiCamera className="text-sm" />
                <span>مسح</span>
              </button>
              <button
                onClick={loadClientData}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="تحديث"
              >
                <FiRefreshCw className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto h-full">
              {tabs[activeTab].content}
            </div>
          </div>
        </div>
      </div>
      
      {/* Support Floating Button */}
      <button
        onClick={handleSupportClick}
        className="fixed bottom-20 left-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all z-40 flex items-center justify-center"
        title="الدعم الفني"
      >
        <FiHeadphones className="w-5.5 h-5.5" />
      </button>

      {/* ═══════════════ Modal: تقديم شكوى ═══════════════ */}
      {complaintModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-lg rounded-3xl p-6 shadow-2xl animate-fade-in" dir="rtl">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                  <FiAlertTriangle size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 font-sans">تقديم شكوى</h2>
                  <p className="text-xs text-gray-500 font-sans">يرجى كتابة تفاصيل المشكلة</p>
                </div>
              </div>
              <button 
                onClick={() => setComplaintModalOpen(false)} 
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-start gap-3 text-red-800 text-sm">
                <FiAlertCircle className="text-red-500 mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="font-bold font-sans">تنبيه هام</p>
                  <p className="mt-0.5 font-sans">إذا قمت بتقديم شكوى كاذبة قد يتم تجميد حسابك.</p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-1.5 block font-bold font-sans">سبب الشكوى</label>
                <textarea
                  value={complaintReason}
                  onChange={(e) => setComplaintReason(e.target.value)}
                  placeholder="اكتب هنا تفاصيل المشكلة أو السبب الذي يجعلك تقدم هذه الشكوى..."
                  rows={4}
                  className="w-full p-4 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none font-sans"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSendComplaint}
                  disabled={submittingComplaint}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition font-sans"
                >
                  {submittingComplaint ? 'جاري الإرسال...' : 'تأكيد وإرسال الشكوى'}
                </button>
                <button
                  onClick={() => setComplaintModalOpen(false)}
                  disabled={submittingComplaint}
                  className="py-3 px-6 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 rounded-xl font-bold text-sm transition font-sans"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <QrScannerModal
        isOpen={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        clientId={user?.id}
        clientWallet={client?.wallet || 0}
        onPurchaseSuccess={() => {
          loadClientData();
          setQrScannerOpen(false);
        }}
        onWalletCodeDetected={(walletCode) => {
          navigate(`/qr-transfer/${walletCode}`);
        }}
      />
    </>
  );
};

export default ClientMainScreen;